import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchAPIKeys,
  createAPIKey,
  revokeAPIKey,
  rotateAPIKey,
  clearNewKeySecret,
  clearRotationResult,
} from '../store/slices/apiKeysSlice';
import { generateTraceId, logUserAction } from '../utils/trace';

// Available scopes for API keys
const AVAILABLE_SCOPES = [
  { value: 'jobs:read', label: 'Read Jobs', description: 'View job status and details' },
  { value: 'jobs:write', label: 'Write Jobs', description: 'Submit and cancel jobs' },
  { value: 'chains:read', label: 'Read Chains', description: 'View chain status and details' },
  { value: 'chains:write', label: 'Write Chains', description: 'Submit chains' },
  { value: 'analytics:read', label: 'Read Analytics', description: 'View analytics and field data' },
  { value: 'analytics:query', label: 'Query Analytics', description: 'Execute SOQL/SQL queries' },
];

function APIKeysPage() {
  const dispatch = useDispatch();
  const { user, activeTenant } = useSelector((state) => state.auth);
  const { keys, isLoading, error, newKeySecret, rotationResult } = useSelector(
    (state) => state.apiKeys
  );

  const [activeTab, setActiveTab] = useState('keys');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState(['jobs:read']);
  const [newKeyExpiry, setNewKeyExpiry] = useState('90');
  const [copiedKey, setCopiedKey] = useState(false);

  const isAdmin = activeTenant?.role === 'admin' || user?.isGodMode;

  useEffect(() => {
    if (activeTenant && isAdmin) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'page_load', { page: 'api-keys' });
      dispatch(fetchAPIKeys({ traceId }));
    }
  }, [dispatch, activeTenant, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
        <p className="text-gray-600">You need admin privileges to manage API keys.</p>
      </div>
    );
  }

  const handleCreateKey = async (e) => {
    e.preventDefault();
    const traceId = generateTraceId();
    logUserAction(traceId, 'create_api_key', { name: newKeyName, scopes: newKeyScopes });
    await dispatch(
      createAPIKey({
        name: newKeyName,
        description: newKeyDescription,
        scopes: newKeyScopes,
        expiresInDays: newKeyExpiry ? parseInt(newKeyExpiry, 10) : null,
        tenantId: activeTenant.tenant_id,
        traceId,
      })
    );
    setNewKeyName('');
    setNewKeyDescription('');
    setNewKeyScopes(['jobs:read']);
    setNewKeyExpiry('90');
  };

  const handleRevokeKey = async (keyId, keyName) => {
    if (window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This action cannot be undone.`)) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'revoke_api_key', { keyId });
      await dispatch(revokeAPIKey({ keyId, traceId }));
    }
  };

  const handleRotateKey = async (keyId, keyName) => {
    if (window.confirm(`Rotate the API key "${keyName}"? The old key will remain valid for 24 hours.`)) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'rotate_api_key', { keyId });
      await dispatch(rotateAPIKey({ keyId, traceId }));
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleScopeToggle = (scope) => {
    setNewKeyScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getKeyStatus = (key) => {
    if (key.revoked_at) return { text: 'Revoked', color: 'text-red-600 bg-red-100' };
    if (key.expires_at && new Date(key.expires_at) < new Date()) {
      return { text: 'Expired', color: 'text-orange-600 bg-orange-100' };
    }
    return { text: 'Active', color: 'text-green-600 bg-green-100' };
  };

  // Filter active and revoked keys
  const activeKeys = keys.filter((k) => !k.revoked_at && (!k.expires_at || new Date(k.expires_at) > new Date()));
  const inactiveKeys = keys.filter((k) => k.revoked_at || (k.expires_at && new Date(k.expires_at) <= new Date()));

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6 text-gray-900">API Keys</h1>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('keys')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'keys'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Your API Keys
          </button>
          <button
            onClick={() => setActiveTab('docs')}
            className={`pb-4 px-2 font-medium transition ${
              activeTab === 'docs'
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            API Documentation
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Keys Tab */}
      {activeTab === 'keys' && (
        <div>
          {/* New Key Secret Modal */}
          {newKeySecret && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl font-bold text-gray-900">API Key Created</h3>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-yellow-800 font-medium mb-2">
                    Copy your API key now. You won't be able to see it again!
                  </p>
                  <div className="flex gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-yellow-300 text-sm font-mono break-all">
                      {newKeySecret}
                    </code>
                    <button
                      onClick={() => copyToClipboard(newKeySecret)}
                      className={`px-3 py-2 rounded transition ${
                        copiedKey
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      {copiedKey ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => {
                    dispatch(clearNewKeySecret());
                    setShowCreateModal(false);
                  }}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Rotation Result Modal */}
          {rotationResult && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <h3 className="text-xl font-bold text-gray-900">API Key Rotated</h3>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 mb-2">
                    Your old key will expire on <strong>{formatDate(rotationResult.old_expires_at)}</strong>.
                  </p>
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    Copy your new API key now. You won't be able to see it again!
                  </p>
                  <div className="flex gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-blue-300 text-sm font-mono break-all">
                      {rotationResult.new_key?.secret_key}
                    </code>
                    <button
                      onClick={() => copyToClipboard(rotationResult.new_key?.secret_key)}
                      className={`px-3 py-2 rounded transition ${
                        copiedKey
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      {copiedKey ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => dispatch(clearRotationResult())}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Active Keys */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="font-bold text-gray-900">Active API Keys</h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition text-sm font-medium"
              >
                Create New Key
              </button>
            </div>
            {isLoading ? (
              <div className="p-8 text-center text-gray-600">Loading...</div>
            ) : activeKeys.length === 0 ? (
              <div className="p-8 text-center text-gray-600">
                No active API keys. Create one to get started.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {activeKeys.map((key) => {
                  const status = getKeyStatus(key);
                  return (
                    <div key={key.key_id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{key.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                              {status.text}
                            </span>
                          </div>
                          {key.description && (
                            <p className="text-sm text-gray-600 mb-2">{key.description}</p>
                          )}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {key.scopes?.map((scope) => (
                              <span
                                key={scope}
                                className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded"
                              >
                                {scope}
                              </span>
                            ))}
                          </div>
                          <div className="text-xs text-gray-500 space-x-4">
                            <span>Prefix: <code className="bg-gray-100 px-1 rounded">{key.key_prefix}</code></span>
                            <span>Created: {formatDate(key.created_at)}</span>
                            {key.expires_at && <span>Expires: {formatDate(key.expires_at)}</span>}
                            {key.last_used_at && <span>Last used: {formatDate(key.last_used_at)}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => handleRotateKey(key.key_id, key.name)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Rotate
                          </button>
                          <button
                            onClick={() => handleRevokeKey(key.key_id, key.name)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Revoke
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Inactive Keys */}
          {inactiveKeys.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-bold text-gray-900">Revoked / Expired Keys</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {inactiveKeys.map((key) => {
                  const status = getKeyStatus(key);
                  return (
                    <div key={key.key_id} className="p-4 opacity-60">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{key.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                              {status.text}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 space-x-4">
                            <span>Prefix: <code className="bg-gray-100 px-1 rounded">{key.key_prefix}</code></span>
                            {key.revoked_at && <span>Revoked: {formatDate(key.revoked_at)}</span>}
                            {key.expires_at && <span>Expired: {formatDate(key.expires_at)}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documentation Tab */}
      {activeTab === 'docs' && (
        <div className="space-y-6">
          {/* Authentication Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Authentication</h2>
            <p className="text-gray-600 mb-4">
              Use your API key in the <code className="bg-gray-100 px-1 rounded">Authorization</code> header:
            </p>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
{`curl -X POST https://api.example.com/api/v1/jobs \\
  -H "Authorization: Bearer jpk_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"idempotency_key": "unique-id", "job_type": {"name": "example"}}'`}
            </pre>
          </div>

          {/* Endpoints Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">API Endpoints</h2>

            <div className="space-y-6">
              {/* Submit Job */}
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">POST</span>
                  <code className="text-sm font-mono">/api/v1/jobs</code>
                </div>
                <p className="text-sm text-gray-600 mb-2">Submit a new job for processing.</p>
                <p className="text-xs text-gray-500">Required scope: <code className="bg-gray-100 px-1 rounded">jobs:write</code></p>
                <details className="mt-2">
                  <summary className="text-sm text-primary-600 cursor-pointer hover:text-primary-800">Show example</summary>
                  <pre className="bg-gray-50 p-3 rounded mt-2 text-xs overflow-x-auto">
{`{
  "idempotency_key": "unique-request-id-123",
  "job_type": {
    "name": "data_processing",
    "version": "1.0"
  },
  "inputs": {
    "file_url": "https://example.com/data.csv"
  },
  "hints": {
    "priority": "high"
  }
}`}
                  </pre>
                </details>
              </div>

              {/* Get Job */}
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">GET</span>
                  <code className="text-sm font-mono">/api/v1/jobs/{'{job_id}'}</code>
                </div>
                <p className="text-sm text-gray-600 mb-2">Get the status and details of a specific job.</p>
                <p className="text-xs text-gray-500">Required scope: <code className="bg-gray-100 px-1 rounded">jobs:read</code></p>
              </div>

              {/* List Jobs */}
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">GET</span>
                  <code className="text-sm font-mono">/api/v1/jobs</code>
                </div>
                <p className="text-sm text-gray-600 mb-2">List all jobs for your tenant.</p>
                <p className="text-xs text-gray-500">Required scope: <code className="bg-gray-100 px-1 rounded">jobs:read</code></p>
              </div>

              {/* Cancel Job */}
              <div className="border-l-4 border-orange-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-0.5 rounded">POST</span>
                  <code className="text-sm font-mono">/api/v1/jobs/{'{job_id}'}/cancel</code>
                </div>
                <p className="text-sm text-gray-600 mb-2">Cancel a running job.</p>
                <p className="text-xs text-gray-500">Required scope: <code className="bg-gray-100 px-1 rounded">jobs:write</code></p>
              </div>

              {/* Submit Chain */}
              <div className="border-l-4 border-green-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded">POST</span>
                  <code className="text-sm font-mono">/api/v1/chains</code>
                </div>
                <p className="text-sm text-gray-600 mb-2">Submit a chain of jobs to run in sequence.</p>
                <p className="text-xs text-gray-500">Required scope: <code className="bg-gray-100 px-1 rounded">chains:write</code></p>
              </div>

              {/* Get Chain */}
              <div className="border-l-4 border-blue-500 pl-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded">GET</span>
                  <code className="text-sm font-mono">/api/v1/chains/{'{chain_id}'}</code>
                </div>
                <p className="text-sm text-gray-600 mb-2">Get the status and details of a chain.</p>
                <p className="text-xs text-gray-500">Required scope: <code className="bg-gray-100 px-1 rounded">chains:read</code></p>
              </div>
            </div>
          </div>

          {/* Response Codes Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Response Codes</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-2 font-medium text-gray-900">Code</th>
                  <th className="pb-2 font-medium text-gray-900">Meaning</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2"><code className="text-green-600">200</code></td>
                  <td className="py-2 text-gray-600">Success</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-green-600">201</code></td>
                  <td className="py-2 text-gray-600">Created (job accepted)</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-green-600">202</code></td>
                  <td className="py-2 text-gray-600">Accepted (job queued)</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-yellow-600">400</code></td>
                  <td className="py-2 text-gray-600">Bad Request - Invalid input</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-red-600">401</code></td>
                  <td className="py-2 text-gray-600">Unauthorized - Invalid or missing API key</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-red-600">403</code></td>
                  <td className="py-2 text-gray-600">Forbidden - Insufficient scope</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-yellow-600">404</code></td>
                  <td className="py-2 text-gray-600">Not Found - Resource doesn't exist</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-red-600">429</code></td>
                  <td className="py-2 text-gray-600">Rate Limited - Too many requests</td>
                </tr>
                <tr>
                  <td className="py-2"><code className="text-red-600">500</code></td>
                  <td className="py-2 text-gray-600">Server Error - Contact support</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Rate Limits Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Rate Limits</h2>
            <p className="text-gray-600 mb-4">
              API keys have a default rate limit of <strong>60 requests per minute</strong>.
              Rate limit headers are included in all responses:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li><code className="bg-gray-100 px-1 rounded">X-RateLimit-Limit</code> - Maximum requests per minute</li>
              <li><code className="bg-gray-100 px-1 rounded">X-RateLimit-Remaining</code> - Requests remaining in window</li>
              <li><code className="bg-gray-100 px-1 rounded">X-RateLimit-Reset</code> - Unix timestamp when limit resets</li>
            </ul>
          </div>

          {/* Best Practices Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Best Practices</h2>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Use idempotency keys</strong> - Always include a unique <code className="bg-gray-100 px-1 rounded">idempotency_key</code> to prevent duplicate job submissions.</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Rotate keys regularly</strong> - Use the rotate feature to update keys without downtime (24h grace period).</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Use minimal scopes</strong> - Only request the scopes you need for your use case.</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Set expiration dates</strong> - Keys with expiration dates are more secure than permanent keys.</span>
              </li>
              <li className="flex items-start gap-2">
                <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span><strong>Handle errors gracefully</strong> - Implement retry logic with exponential backoff for transient errors.</span>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Create Key Modal */}
      {showCreateModal && !newKeySecret && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-gray-900">Create API Key</h3>
            <form onSubmit={handleCreateKey}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Key Name *
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Production API Key"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newKeyDescription}
                  onChange={(e) => setNewKeyDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g., Used by our data pipeline"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scopes *
                </label>
                <div className="space-y-2">
                  {AVAILABLE_SCOPES.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newKeyScopes.includes(scope.value)}
                        onChange={() => handleScopeToggle(scope.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{scope.label}</div>
                        <div className="text-xs text-gray-500">{scope.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {newKeyScopes.length === 0 && (
                  <p className="text-sm text-red-600 mt-1">Select at least one scope</p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration
                </label>
                <select
                  value={newKeyExpiry}
                  onChange={(e) => setNewKeyExpiry(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">1 year</option>
                  <option value="">Never expires</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={newKeyScopes.length === 0 || isLoading}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default APIKeysPage;
